import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { usePopup } from '../../../../hooks/usePopup'
import type { SearchContentProps } from '../searchContent/searchContent'
import { SearchContent } from '../searchContent/searchContent'
import styles from './mobileSearchOverlay.module.css'

interface MobileSearchOverlayProps extends SearchContentProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSearchOverlay({
  isOpen,
  onClose,
  ...searchContentProps
}: MobileSearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { shouldRender, dataState, handleClose } = usePopup({
    isOpen,
    onClose,
    animationDuration: 200,
  })

  // Автофокус при открытии
  useEffect(() => {
    if (!shouldRender || dataState === 'closing') return

    const frameId = window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [shouldRender, dataState])

  // Закрытие по Escape
  useEffect(() => {
    if (!shouldRender) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [shouldRender, handleClose])

  if (!shouldRender) return null

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      data-state={dataState}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Close search"
        onClick={handleClose}
      >
        ✕
      </button>
      <div className={styles.panel} data-state={dataState}>
        <SearchContent {...searchContentProps} ref={inputRef} />
      </div>
    </div>,
    document.body
  )
}
